import React, { useState } from "react";
import { ChevronDown, Trash2 } from "lucide-react";
import { CARD_CLASS } from "../../../common/CertificateOnboarding/utils/styles";
import SubjectVariableEditor from "./SubjectVariableEditor";
import AssetVariableEditor from "./AssetVariableEditor";
import QrVariableEditor from "./QrVariableEditor";
import { EDITOR_KIND } from "../../../common/CertificateOnboarding/utils/variables";

function VariableCard({
  variableKey,
  attrs,
  editorKind,
  setVariableConfig,
  qrCodeType,
  onQrCodeTypeChange,
  isUserAdded = false,
  onRemove,
  t,
  defaultOpen = false,
  hideSvgPositioning = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const title = attrs["data-label"] || variableKey;
  const typeChip = attrs["data-type"] || editorKind;
  const formTypeChip = attrs["data-form-type"] || "";

  return (
    <div
      className={`${CARD_CLASS} transition-shadow duration-200 ${
        isOpen
          ? "overflow-visible shadow-md ring-1 ring-cfPrimary/10"
          : "overflow-hidden"
      }`}
    >
      <div className="w-full flex items-center gap-2 px-4 py-3.5 sm:px-5 hover:bg-cfSecondary/50 transition-colors">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="min-w-0 flex-1 flex items-center justify-between gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary rounded-md"
          aria-expanded={isOpen}
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-cfHeadingText truncate">
              {title}
            </p>
            <p className="text-xs text-cfGlobalText mt-0.5 truncate">
              {variableKey}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isUserAdded && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-cfPrimaryMuted text-cfPrimary border border-cfPrimaryBorder">
                {t("certificateOnboarding:custom_attribute_badge")}
              </span>
            )}
            <span className="text-xs px-2.5 py-0.5 rounded-full bg-cfSecondary text-cfHeadingText border border-cfCardBorder capitalize">
              {typeChip}
            </span>
            {formTypeChip && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-cfPrimaryMuted text-cfPrimary border border-cfPrimaryBorder capitalize">
                {formTypeChip}
              </span>
            )}
            <ChevronDown
              className={`w-5 h-5 text-cfGlobalText transition-transform duration-200 ease-out ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-cfGlobalText hover:text-cfStatusBlockedText hover:bg-cfSecondary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
            aria-label={t("certificateOnboarding:remove_data_attribute")}
          >
            <Trash2 className="w-4 h-4" aria-hidden />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5 border-t border-cfCardBorder pt-4 bg-cfSecondary/30">
          {editorKind === EDITOR_KIND.SUBJECT && (
            <SubjectVariableEditor
              variableKey={variableKey}
              attrs={attrs}
              setVariableConfig={setVariableConfig}
              t={t}
              hideSvgPositioning={hideSvgPositioning}
            />
          )}
          {editorKind === EDITOR_KIND.ASSET && (
            <AssetVariableEditor
              variableKey={variableKey}
              attrs={attrs}
              setVariableConfig={setVariableConfig}
              t={t}
            />
          )}
          {editorKind === EDITOR_KIND.QR && (
            <QrVariableEditor
              variableKey={variableKey}
              attrs={attrs}
              setVariableConfig={setVariableConfig}
              qrCodeType={qrCodeType}
              onQrCodeTypeChange={onQrCodeTypeChange}
              t={t}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default VariableCard;
